/**
 * Gradient definitions for the Duckbill design system
 */

export type GradientConfig = {
  colors: readonly [string, string, ...string[]];
  end: { x: number; y: number };
  locations: readonly [number, number, ...number[]];
  start: { x: number; y: number };
};

/**
 * Primary gradient used for accent elements like buttons and interactive components
 * CSS: linear-gradient(135deg, #91DDC5 21.18%, #E3EDF8 52.8%, #EBFC72 89.37%)
 * Colors: Mint Green → Light Blue → Lime Yellow
 */
const primary: GradientConfig = {
  colors: ['#91DDC5', '#E3EDF8', '#EBFC72'],
  end: { x: 1, y: 1 },
  locations: [0.2118, 0.528, 0.8937],
  start: { x: 0, y: 0 },
} as const;

const secondary: GradientConfig = {
  colors: ['#EBFC72', '#F4F4EF', '#E3EDF8', '#C1E1FF'],
  end: { x: 1, y: 1 },
  locations: [0, 0.4231, 0.649, 1],
  start: { x: 0, y: 0 },
} as const;

const card: GradientConfig = {
  colors: ['#F8FBCD', '#E5EDF7', '#E5EDF7'],
  end: { x: 0, y: 1 },
  locations: [0, 0.7109, 0.9],
  start: { x: 0, y: 0 },
} as const;

const giftBanner = {
  colors: ['#E9F7F1', '#F4FBF8', '#F9F9F7'],
  css: 'linear-gradient(90deg, #E9F7F1 0%, #F4FBF8 52%, #F9F9F7 100%)',
  end: { x: 1, y: 0 },
  locations: [0, 0.52, 1],
  start: { x: 0, y: 0 },
} as const satisfies GradientConfig & { css: string };

const progressBar: GradientConfig = {
  colors: ['#55BDEA', '#55BDEA'],
  end: { x: 1, y: 0 },
  locations: [0, 1],
  start: { x: 0, y: 0 },
} as const;

const progressBarLow: GradientConfig = {
  colors: ['#FF6B7A', '#FF6B7A'],
  end: { x: 1, y: 0 },
  locations: [0, 1],
  start: { x: 0, y: 0 },
} as const;

export const gradients = {
  card,
  giftBanner,
  primary,
  progressBar,
  progressBarLow,
  secondary,
} as const;

export type GradientName = keyof typeof gradients;
